import React, { FC, useCallback, useMemo } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import { produce } from 'immer';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import audiencesResource from 'src/actions/audiences/audiencesResource';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import generateId from 'src/lib/generateId';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import helpCenterResource, { Section as SectionType } from 'src/actions/helpCenter/helpCenterResource';
import Input from 'src/presentation/components/forms/Input';
import { isLoadingStatus, STATUS_LOADING, STATUS_PERSISTING } from 'src/presentation/hooks/useResource';
import { default as Languages } from '@ad-scripts/domain/models/languages/answer_languages';
import MultiSelect from 'src/presentation/components/forms/MultiSelect';
import { RootState } from 'src/actions/configuration/rootReducer';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import usePublicAnswersFilter from 'src/presentation/components/pages/shared/hooks/usePublicAnswersFilter';
import {
  usePublicAnswersSearch,
  useAnswerTitles,
} from 'src/presentation/components/pages/shared/hooks/usePublicAnswers';
import useResource from 'src/presentation/hooks/useResource';
import useRouter from 'src/presentation/hooks/useRouter';

type RouteParams = {
  configId: string;
};

const HelpCenterSectionEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { configId } = useParams<RouteParams>();
  const { value, persistingState } = useDocumentResource(helpCenterResource, { fetch: false });
  const helpCenterConfig = find(value.siteConfigs, { id: configId as string } as any);

  const helpCenterLanguage = get(helpCenterConfig, 'lng', '');
  const helpCenterAudienceId = get(helpCenterConfig, 'audienceId', '');
  const helpCenterId = get(helpCenterConfig, 'id', '');

  const add = helpCenterResource.effects.add;
  const update = helpCenterResource.effects.update;
  const handleOnSave = useOnSave({
    resetErrors: helpCenterResource.actions.resetErrors,
    validate: helpCenterResource.effects.validate,
  });

  const selectedAnswerIds = get(item, 'answerIds', []);

  const [answerTitlesLoadingState, answerTitles] = useAnswerTitles(helpCenterId);
  const selectedAnswers = answerTitles.filter(answerTitle => selectedAnswerIds.includes(answerTitle.id));

  const isLoading = answerTitlesLoadingState === STATUS_LOADING;

  const defaultItem = {
    header: '',
    answerIds: [],
    lng: helpCenterLanguage,
    audienceId: helpCenterAudienceId,
  };

  const formContext = useForm({
    add,
    onSave: handleOnSave,
    defaultItem,
    errors,
    item,
    resetErrors: helpCenterResource.actions.resetErrors,
    root,
    update,
  });

  const { isNew, onClose, onSave } = formContext;

  const audiences = useResource(audiencesResource).values;
  const audience = helpCenterAudienceId ? find(audiences, { id: helpCenterAudienceId }) : {};

  return isLoading ? (
    <Spinner />
  ) : (
    <Form context={formContext}>
      <H1>{isNew ? 'Create' : 'Edit'} Section</H1>

      <Body>
        <Section description="Arrange Answers in the order you want them to appear in this section.">
          <Row>
            <Input
              data-aid="section-name"
              attr="header"
              autoFocus
              label="Section Name"
              description="The Section Name appears as the heading of this section in the Help Center."
            />
          </Row>
          <Row>
            <AnswersSelector
              audience={audience}
              language={helpCenterLanguage}
              root={root}
              selectedAnswers={selectedAnswers}
            />
          </Row>
        </Section>
      </Body>
      <Footer>
        <Button data-aid="help-center-section-cancel" type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid="help-center-section-save" onClick={onSave} isLoading={persistingState === STATUS_PERSISTING}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

const AnswersSelector = ({ selectedAnswers, root, language, audience }) => {
  const audienceId = audience?.id;

  const [answersLoadingState, answerOptions] = usePublicAnswersSearch(language, audienceId);
  const selectedItemsLabelHash = useMemo(
    () => reduce(selectedAnswers, (acc, { id, name }) => ({ ...acc, [id]: name }), {}),
    [selectedAnswers]
  );

  const onSearchChanged = usePublicAnswersFilter({ language, audienceId });

  const description = (
    <>
      You are searching {audience ? <b>{audience.name}</b> : null} public answers in <b>{Languages[language]}</b>. If
      you&apos;d like to change your language preferences,{' '}
      <Link to={root.replace('/sections', '')}>edit your Help Center</Link>.
    </>
  );

  return (
    <MultiSelect
      data-aid="section-answerIds"
      attr="answerIds"
      delay={500}
      description={description}
      isLoading={isLoadingStatus(answersLoadingState)}
      label="Answers"
      labelsHash={selectedItemsLabelHash}
      onSearchChanged={onSearchChanged}
      options={answerOptions}
    />
  );
};

function useOnSave({ resetErrors, validate }) {
  const dispatch = useDispatch();
  const {
    match: { params },
  }: {
    match: { params: { configId: string; id: string } };
  } = useRouter();
  const helpCenterState: any = useSelector((state: RootState) => state.helpCenter.value);

  return useCallback(
    ({ isNew, workingState }) => {
      dispatch(resetErrors());
      if (!dispatch(validate(workingState, SectionType))) {
        return;
      }

      const currentSiteConfig: any = find(helpCenterState.siteConfigs, { id: params.configId as string } as any);
      const newSiteConfig = produce(currentSiteConfig, draft => {
        if (isNew) {
          const draftSection = { ...workingState, id: generateId() };
          draft.sections.push(draftSection);
        } else {
          const section = find(draft.sections, { id: params.id });
          section.answerIds = workingState.answerIds;
          section.header = workingState.header;
        }
      });
      dispatch(helpCenterResource.effects.replaceSiteConfig(newSiteConfig));
    },
    [dispatch, helpCenterState, params.configId, params.id, resetErrors, validate]
  );
}

export default HelpCenterSectionEditor;
